import { Link } from "react-router-dom";
import { ArrowUpRight, MessageCircle, Phone, Sparkles, ShieldCheck, Clock, Globe2, Award, HandshakeIcon, Users } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { CONTACT } from "../i18n/translations";
import Reveal from "../components/Reveal";
import SectionTitle from "../components/SectionTitle";
import DivisionCard from "../components/DivisionCard";

const HERO_IMG = "https://images.unsplash.com/photo-1695229738510-9e2b4810a57b?crop=entropy&cs=srgb&fm=jpg";
const EUROPATH_IMG = "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?crop=entropy&cs=srgb&fm=jpg";
const DIGITIA_IMG = "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1400&q=70";
const PROCESS_IMG = "https://images.unsplash.com/photo-1617122287896-8638ac5c5414?crop=entropy&cs=srgb&fm=jpg";

const Home = () => {
  const { t } = useLang();

  const whyIcons = [Award, Users, ShieldCheck, Clock, Globe2, Sparkles, HandshakeIcon];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden bg-[#0A192F] isolate">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="Valencia, Spain" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-[#0A192F]/70" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A192F]/30 via-[#0A192F]/60 to-[#0A192F]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <Reveal>
            <p className="text-[11px] sm:text-xs uppercase tracking-[0.4em] text-[#D4AF37]">{t.hero.overline}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-serif text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-5xl">
              {t.hero.title_a}{" "}
              <span className="italic text-[#D4AF37]">{t.hero.title_b}</span>{" "}
              {t.hero.title_c}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-base sm:text-lg text-gray-300 leading-relaxed">
              {t.hero.sub}
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="hero-cta-consultation"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A192F] px-7 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-white transition-colors"
              >
                {t.common.free_consultation} <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a
                href={CONTACT.whatsapp_link}
                data-testid="hero-cta-whatsapp"
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-transparent text-white px-7 py-4 text-xs uppercase tracking-[0.25em] font-semibold border border-white/40 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> {t.common.whatsapp_now}
              </a>
              <a
                href={CONTACT.whatsapp_link}
                data-testid="hero-cta-call"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-transparent text-white px-7 py-4 text-xs uppercase tracking-[0.25em] font-semibold border border-white/40 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              >
                <Phone className="w-4 h-4" /> {CONTACT.phone}
              </a>
            </div>
          </Reveal>

          {/* Division mini cards */}
          <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Reveal delay={0.35}>
              <Link to="/services" data-testid="hero-europath-card" className="group block border border-white/15 bg-white/[0.04] backdrop-blur p-7 hover:border-[#D4AF37] transition-colors">
                <p className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]">{t.common.primary_division}</p>
                <h3 className="mt-3 font-serif text-2xl sm:text-3xl">Europath Consultancy</h3>
                <p className="mt-2 text-sm text-gray-300">{t.hero.europath_label}.</p>
                <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white group-hover:text-[#D4AF37]">
                  {t.common.learn_more} <ArrowUpRight className="w-4 h-4" />
                </span>
              </Link>
            </Reveal>
            <Reveal delay={0.45}>
              <Link to="/digital" data-testid="hero-digitia-card" className="group block border border-white/15 bg-white/[0.04] backdrop-blur p-7 hover:border-[#D4AF37] transition-colors">
                <p className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]">{t.common.secondary_division}</p>
                <h3 className="mt-3 font-serif text-2xl sm:text-3xl">Digitia</h3>
                <p className="mt-2 text-sm text-gray-300">{t.hero.digitia_label}.</p>
                <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white group-hover:text-[#D4AF37]">
                  {t.common.learn_more} <ArrowUpRight className="w-4 h-4" />
                </span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DIVISIONS */}
      <section className="bg-white py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTitle
            overline={t.services.overline}
            title={t.services.title}
            sub={t.services.sub}
          />

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
            <DivisionCard
              tone="primary"
              testid="europath-card"
              image={EUROPATH_IMG}
              badge={t.services.primary}
              name={t.divisions.europath.name}
              tag={t.divisions.europath.tag}
              intro={t.divisions.europath.intro}
              items={t.divisions.europath.items.slice(0, 6)}
              link="/services"
            />
            <DivisionCard
              tone="secondary"
              testid="digitia-card"
              image={DIGITIA_IMG}
              badge={t.services.secondary}
              name={t.divisions.digitia.name}
              tag={t.divisions.digitia.tag}
              intro={t.divisions.digitia.intro}
              items={t.divisions.digitia.items.slice(0, 6)}
              link="/digital"
            />
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-gray-100 py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTitle overline="Why us" title={t.why.title} sub={t.why.sub} />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
            {t.why.items.map((it, i) => {
              const Icon = whyIcons[i % whyIcons.length];
              return (
                <Reveal key={it.t} delay={i * 0.05}>
                  <div data-testid={`why-item-${i}`} className="bg-white p-8 h-full hover:bg-[#0A192F] hover:text-white transition-colors group">
                    <Icon className="w-7 h-7 text-[#D4AF37]" />
                    <h4 className="mt-5 font-serif text-xl">{it.t}</h4>
                    <p className="mt-3 text-sm text-gray-600 group-hover:text-gray-300 leading-relaxed">{it.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="relative py-24 sm:py-32 overflow-hidden bg-[#0A192F] isolate">
        <div className="absolute inset-0 z-0">
          <img src={PROCESS_IMG} alt="European architecture" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-[#0A192F]/85" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.3em] font-semibold text-[#D4AF37]">Our Process</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-2xl">{t.process.title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-gray-300 leading-relaxed">{t.process.sub}</p>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10">
            {t.process.steps.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.06}>
                <div data-testid={`process-step-${i+1}`} className="bg-[#0A192F] p-8 h-full">
                  <p className="font-serif text-5xl text-[#D4AF37] leading-none">0{i + 1}</p>
                  <h4 className="mt-6 font-serif text-xl">{s.t}</h4>
                  <p className="mt-3 text-sm text-gray-300 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-12">
              <Link
                to="/contact"
                data-testid="process-cta"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A192F] px-7 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-white transition-colors"
              >
                {t.common.free_consultation} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] tracking-tight leading-tight">
              Let&apos;s advise you <span className="italic text-[#D4AF37]">beyond borders</span> — starting today.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/contact" data-testid="bottom-cta-consult" className="inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white px-7 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#D4AF37] hover:text-[#0A192F] transition-colors">
                {t.common.free_consultation}
              </Link>
              <a href={CONTACT.whatsapp_link} data-testid="bottom-cta-whatsapp" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 border border-[#0A192F] text-[#0A192F] px-7 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#0A192F] hover:text-white transition-colors">
                <MessageCircle className="w-4 h-4" /> {t.common.whatsapp_now}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
